import { createFileRoute, Link } from '@tanstack/react-router';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';

// `question_detail` is the admin-only read and serves `QuestionAdmin` — the
// stored body, the authoring form and the answer key (content/router.py:740).
// The student shape (`Question`) no longer carries `is_correct` at all, so the
// answer type has to come off this shape, not off `Question`.
type QuestionAdmin = components['schemas']['QuestionAdmin'];
type Answer = NonNullable<QuestionAdmin['answers']>[number];

export const Route = createFileRoute('/_admin/questions/$questionId')({
  component: QuestionDetail,
});

function QuestionDetail() {
  const { questionId } = Route.useParams();
  const questionIdNum = Number(questionId);

  const { data, isLoading, error } = api.questionDetail.useQuery({
    path: { question_id: questionIdNum },
  });

  if (isLoading) return <Message>Loading question...</Message>;
  if (error) return <Message $error>Error loading question</Message>;
  if (!data) return <Message $error>Question not found</Message>;

  return (
    <Container>
      <BackLink>
        <Link to="/questions" search={{ lesson: data.lesson_id }}>
          &larr; Back to questions
        </Link>
      </BackLink>

      <PageTitle>Question #{data.id}</PageTitle>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Type</FieldLabel>
            <FieldValue>{data.type}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Order</FieldLabel>
            <FieldValue>{data.order}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Score</FieldLabel>
            <FieldValue>{data.score}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Status</FieldLabel>
            <FieldValue>{data.status}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Lesson</FieldLabel>
            <FieldValue>
              <Link
                to="/lessons/$lessonId"
                params={{ lessonId: String(data.lesson_id) }}
              >
                #{data.lesson_id}
              </Link>
            </FieldValue>
          </Field>
          {data.topic_id != null && (
            <Field>
              <FieldLabel>Topic</FieldLabel>
              <FieldValue>
                <Link
                  to="/topics/$topicId"
                  params={{ topicId: String(data.topic_id) }}
                >
                  #{data.topic_id}
                </Link>
              </FieldValue>
            </Field>
          )}
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>Question</SectionTitle>
        <QuestionText>{data.text || <em>(no question text)</em>}</QuestionText>
      </Section>

      {data.cloze_replacer != null && (
        <Section>
          <SectionTitle>Cloze form (served to students)</SectionTitle>
          {/* Students answer against `cloze_replacer`, not the stored body above:
              one `@@wpProQuizCloze@@` marker per blank, matching the answer rows
              47/47, where the body's underscore runs disagree on 4 published
              questions (content/service.py:706-722). The CMS is the only place
              the two can be compared. No type test is needed —
              `cloze_replacer` is non-null only on FILL_IN_BLANK (46 of 47,
              measured 2026-08-17). */}
          <QuestionText>{data.cloze_replacer}</QuestionText>
        </Section>
      )}

      {data.hint !== '' && (
        <Section>
          <SectionTitle>Hint</SectionTitle>
          <QuestionText>{data.hint}</QuestionText>
        </Section>
      )}

      {data.answers && data.answers.length > 0 && (
        <Section>
          <SectionTitle>Answers</SectionTitle>
          <AnswerList>
            {data.answers.map((a, i) => (
              <AnswerView key={i} answer={a} />
            ))}
          </AnswerList>
        </Section>
      )}
    </Container>
  );
}

function AnswerView({ answer }: { answer: Answer }) {
  return (
    <AnswerRow $correct={answer.is_correct}>
      <AnswerMark>{answer.is_correct ? '✓' : ''}</AnswerMark>
      <AnswerText>{answer.text}</AnswerText>
      {answer.is_correct && <small>correct</small>}
    </AnswerRow>
  );
}

const Container = styled.div``;

const QuestionText = styled.div`
  padding: var(--inner-padding);
  font-size: 0.9375rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--inner-padding);
  gap: 0.25rem;
`;

const AnswerRow = styled.div<{ $correct: boolean }>`
  display: grid;
  grid-template-columns: 1.5rem 1fr auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  border-left: 3px solid
    ${(p) => (p.$correct ? 'var(--success)' : 'transparent')};
  background: ${(p) =>
    p.$correct
      ? 'color-mix(in srgb, var(--success) 14%, transparent)'
      : 'transparent'};
  color: var(--grey-700);
`;

const AnswerMark = styled.span`
  font-weight: 700;
  text-align: center;
  color: var(--success);
`;

const AnswerText = styled.span``;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
